import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {GenericResponseJSON} from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {listToReadable} from 'util/NameUtil';

// Keep types in sync with benchling/sample_tests/integrations/data_input/test_order_results_from_worksheet_utils.py
export enum ResultTestOrderErrorType {
  DUPLICATE_RESULTS = 'DUPLICATE_RESULTS',
  RESULTS_NOT_ALLOWED = 'RESULTS_NOT_ALLOWED',
  INVALID_RESULTS = 'INVALID_RESULTS',
}

interface DocumentWithResultCount {
  documentId: string;
  documentName: string;
  documentUrl: string;
  invalidResultCount: number;
  duplicateResultCount: number;
}

interface WorksheetTestOrderResultError {
  documentsWithErrors: Array<DocumentWithResultCount>;
  testOrderResultsErrorType: ResultTestOrderErrorType;
  userMessage: string;
  hasAdditionalInvalidResults: boolean;
}

export function catchWorksheetTestOrderResultError(err: {
  responseJSON?: GenericResponseJSON;
}): JSX.Element | null {
  if (isWorksheetTestOrderResultError(err.responseJSON)) {
    return <WorksheetTestOrderResultErrorBanner error={err.responseJSON} />;
  }
  return null;
}

export function isWorksheetTestOrderResultError(
  errorResponseJson?: GenericResponseJSON
): errorResponseJson is WorksheetTestOrderResultError {
  return (
    errorResponseJson != null &&
    (errorResponseJson as WorksheetTestOrderResultError).testOrderResultsErrorType != null
  );
}

export function WorksheetTestOrderResultErrorBanner({
  error,
}: {
  error: WorksheetTestOrderResultError;
}): JSX.Element {
  const errorHeader =
    error.testOrderResultsErrorType === ResultTestOrderErrorType.DUPLICATE_RESULTS
      ? `Cannot send for review due to duplicate${error.hasAdditionalInvalidResults ? ' and invalid' : ''} results`
      : error.testOrderResultsErrorType === ResultTestOrderErrorType.INVALID_RESULTS
        ? 'Cannot send for review due to invalid results'
        : 'Cannot send for review due to unarchived results';
  const getResultsStr = (doc: DocumentWithResultCount): string => {
    const errors = [];
    if (doc.duplicateResultCount > 0) {
      errors.push(`${doc.duplicateResultCount} duplicate ${pluralize(doc.duplicateResultCount, 'result')}`);
    }
    if (doc.invalidResultCount > 0) {
      errors.push(`${doc.invalidResultCount} invalid ${pluralize(doc.invalidResultCount, 'result')}`);
    }
    if (errors.length === 0) {
      return '';
    }
    return ` (${listToReadable(errors)})`;
  };
  const supportingInfo = (
    <div>
      <p>{error.userMessage}</p>
      <p>
        <b>Archive analyses for the following steps:</b>
      </p>
      {error.documentsWithErrors.map((doc) => (
        <li key={doc.documentId}>
          <a target="blank" rel="noopener noreferrer" href={doc.documentUrl}>
            {doc.documentName}
          </a>
          {getResultsStr(doc)}
        </li>
      ))}
    </div>
  );
  return (
    <InlineBanner bannerType={InlineBannerType.ERROR} text={errorHeader} supportingInfo={supportingInfo} />
  );
}
